import { DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR } from "@/constants";
import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
import { growaveConfigurationToken } from "@/tokens";
import { isShadowRoot } from "@/utils/isShadowRoot/isShadowRoot";
import { RewardsTranslationNamespaces } from "@apps/rewards/constants/constants";
import RewardsCurrencyFormatter from "@apps/rewards/lib/RewardsCurrencyFormatter";
import RewardsProgramAccountQuery from "@apps/rewards/modules/ProgramAccount/query/RewardsProgramAccountQuery";
import RewardsReferralProgramQuery from "@apps/rewards/modules/ReferralProgram/queries/RewardsReferralProgramQuery";
import RewardsReferralProgramService from "@apps/rewards/modules/ReferralProgram/services/RewardsReferralProgramService";
import RewardsStorefrontValuesService from "@apps/rewards/modules/StorefrontValues/service/RewardsStorefrontValuesService";
import RewardsDiResolverService from "@apps/rewards/services/RewardsDiResolverService/RewardsDIResolverService";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { AuthenticationModule } from "@modules/authentication/AuthenticationModule";
import { CommonModule } from "@modules/common_module/CommonModule";
import { I18nModule } from "@modules/i18n/I18nModule";
import { injectable } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";
import type { WidgetInterface } from "@interfaces/WidgetInterface";

import RewardsReferralProgramPageWidgetComponent from "./RewardsReferralProgramPageWidget.svelte";
import type { RewardsReferralProgramPageWidgetConfig } from "./RewardsReferralProgramPageWidgetConfigSchema";

@injectable()
class RewardsReferralProgramPageWidget implements WidgetInterface {
    constructor(
        private readonly config: RewardsReferralProgramPageWidgetConfig,
        private readonly logger: LoggerInterface
    ) {
        this.logger.debug("RewardsReferralProgramPageWidget is initialized");
    }

    async init(): Promise<this> {
        const namespace =
            RewardsTranslationNamespaces.rewardsReferralProgramPageWidget;
        const pointsNamespace =
            RewardsTranslationNamespaces.commonCriticalPoints;

        const diResolverService = new RewardsDiResolverService();
        const {
            i18nModule,
            rewardsReferralProgramService,
            rewardsStorefrontValuesService,
            rewardsReferralProgramQuery,
            rewardsProgramAccountQuery,
            rewardsCurrencyFormatter,
            growaveConfiguration,
            commonModule,
            authenticationModule,
            customizationManager,
        } = diResolverService.resolveAll({
            i18nModule: I18nModule,
            rewardsReferralProgramService: RewardsReferralProgramService,
            rewardsStorefrontValuesService: RewardsStorefrontValuesService,
            rewardsReferralProgramQuery: RewardsReferralProgramQuery,
            rewardsProgramAccountQuery: RewardsProgramAccountQuery,
            rewardsCurrencyFormatter: RewardsCurrencyFormatter,
            growaveConfiguration: growaveConfigurationToken,
            commonModule: CommonModule,
            authenticationModule: AuthenticationModule,
            customizationManager: CustomizationManager,
        });

        const { i18n, i18nApiLoaderFactory, i18nCriticalLoaderFactory } =
            i18nModule.exports;
        i18n.registerLoader(
            namespace,
            i18nApiLoaderFactory.getLoader(namespace)
        );
        i18n.registerLoader(
            pointsNamespace,
            i18nCriticalLoaderFactory.getLoader(pointsNamespace)
        );
        const pointsName$ = i18n.selectT(pointsNamespace, "points_name_plural");

        const { locationController, urlsBuilder, designModeProvider } =
            commonModule.exports;

        const element = this.config.target;
        const shadowRoot = isShadowRoot(
            this.config.isWidgetShadowRootEnabled,
            growaveConfiguration.isShadowRootDisabledGlobally
        )
            ? element.shadowRoot || element.attachShadow({ mode: "open" })
            : null;

        const customizationManagerConfig = {
            widgetName:
                REWARDS_WIDGET_NAMES.REWARDS_REFERRAL_PROGRAM_PAGE_WIDGET,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStylesFromDesignMode({
            element,
            shadowRoot,
            scriptSelector: DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR,
        });
        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );

        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        new RewardsReferralProgramPageWidgetComponent({
            target: shadowRoot || element,
            props: {
                rewardsReferralProgramService,
                rewardsStorefrontValuesService,
                rewardsReferralProgramQuery,
                rewardsProgramAccountQuery,
                rewardsCurrencyFormatter,
                growaveConfiguration,
                locationController,
                urlsBuilder,
                authenticationInfoProvider:
                    authenticationModule.exports.authenticationInfoProvider,
                pointsName$,
                namespace,
                customerImageUrl: this.config.customerImageUrl,
                designModeProvider,
                afterWidgetInject: () => {
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    );
                },
            },
        });

        return this;
    }
}

export default RewardsReferralProgramPageWidget;
